import { ArrowRight, Zap } from "lucide-react";

export default function Hero() {
  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden pt-20"
    >
      {/* Background — OmniMuscle Phoenix logo as the hero centerpiece */}
      <div className="absolute inset-0 bg-[#0F1421]">
        <img
          src="https://customer-assets.emergentagent.com/job_omni-brand-1/artifacts/vm0c2iaa_IMG_20260520_082121.png"
          alt="OMNIMUSCLE Phoenix"
          className="absolute right-0 top-1/2 -translate-y-1/2 h-[120%] w-auto max-w-[70%] object-contain opacity-90 select-none pointer-events-none"
          draggable="false"
        />
        {/* Left-side fade so the headline reads cleanly over the logo's edge */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/85 via-50% to-transparent" />
        {/* Subtle grid + radial finish */}
        <div className="absolute inset-0 hero-grid opacity-25" />
        <div className="absolute inset-0 hero-radial opacity-70" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
        <div className="max-w-3xl">
          <div
            data-testid="hero-tag"
            className="inline-flex items-center gap-2 px-4 py-2 border border-[#D4AF37]/40 text-[#D4AF37] mb-8 fade-up"
          >
            <Zap size={14} />
            <span className="font-accent text-xs tracking-[0.3em] uppercase">
              Flying Higher · Mind · Body · Soul
            </span>
          </div>

          {/* Official phoenix medallion */}
          <div
            data-testid="hero-logo"
            className="inline-block mb-6 fade-up-delay-1 p-1 rounded-full border-4 border-[#6D28D9] shadow-[0_0_60px_rgba(109,40,217,0.45)] -rotate-1 bg-black/30"
          >
            <img
              src="https://customer-assets-rejwkqb3.emergentagent.net/job_omni-brand-1/artifacts/84dtbvvf_1783524637283.png"
              alt="OMNIMUSCLE phoenix medallion"
              className="block h-24 sm:h-28 md:h-36 w-auto rounded-full"
            />
          </div>

          <div data-testid="hero-tagline" className="inline-block mb-8 fade-up-delay-1">
            <span className="text-[10px] md:text-xs uppercase tracking-[0.4em] font-bold text-[#6D28D9] border-y border-[#6D28D9] py-1 px-3 bg-black/40">
              Holistic Healing
            </span>
          </div>

          <h1
            data-testid="hero-title"
            className="font-display text-6xl sm:text-7xl md:text-8xl lg:text-9xl text-white leading-[0.85] mb-6 fade-up-delay-1"
          >
            Fly
            <br />
            <span className="text-[#D4AF37] wordmark-shadow">Higher.</span>
            <br />
            Train Harder.
          </h1>

          <p
            data-testid="hero-sub"
            className="text-lg md:text-xl text-neutral-300 max-w-2xl mb-10 leading-relaxed fade-up-delay-2"
          >
            <span className="font-bold" style={{ color: "#4C1D95", WebkitTextStroke: "0.5px #D4AF37" }}>
              OMNI<span className="text-[#DC2626]">MUSCLE</span>
            </span>{" "}— holistic healing for mind, body &amp; soul.{" "}
            <span className="text-white">🌈 Flying higher to inspire positive change 🌈</span> Programs,
            coaching, and supplement stacks built by{" "}
            <span className="font-bold" style={{ color: "#4C1D95", WebkitTextStroke: "0.5px #D4AF37" }}>
              @OmniMuscle<span className="text-[#DC2626]">_Bodybuilding</span>
            </span>{" "}
            for the ones ready to rise.
          </p>

          <div className="flex flex-wrap gap-4 fade-up-delay-3">
            <button
              data-testid="hero-cta-primary"
              onClick={() => scrollTo("ai-coach")}
              className="btn-omni btn-red"
            >
              Get My AI Plan <ArrowRight size={18} />
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => scrollTo("programs")}
              className="btn-omni btn-outline-gold"
            >
              View Programs
            </button>
          </div>

          {/* Stats strip */}
          <div className="grid grid-cols-3 gap-6 mt-20 max-w-2xl border-t border-zinc-800 pt-8 fade-up-delay-3">
            {[
              { n: "6", l: "Athletes Coached" },
              { n: "2", l: "Show Wins" },
              { n: "100%", l: "Iron Mindset" },
            ].map((s) => (
              <div key={s.l} data-testid={`hero-stat-${s.l}`}>
                <div className="font-display text-4xl md:text-5xl text-[#D4AF37]">
                  {s.n}
                </div>
                <div className="text-xs uppercase tracking-widest text-neutral-400 mt-1">
                  {s.l}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
